import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Folder, FolderOpen, Lock, FileText,
  FileSpreadsheet, FileSignature, Image as ImageIcon, Video, Map as MapIcon,
  Download, Pencil, Trash2, Search, X, Calendar, Archive,
  Globe, ShieldCheck, Star,
} from "lucide-react";
import {
  drListFiles, drDeleteFile, drTeamDownloadUrl, drCategoriesSummary,
} from "../../lib/api";
import { fmtDate } from "../../lib/format";

// ---------------------------------------------------------------------------
// Visibility-Badges
// ---------------------------------------------------------------------------
const VIS_META = {
  sofort:          { label: "Sofort",       color: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: Globe },
  nach_NDA:        { label: "Nach NDA",     color: "bg-gold/15 text-gold-dark border-gold/40",          icon: ShieldCheck },
  nach_indikativ:  { label: "Nach Gebot",   color: "bg-sky-50 text-sky-900 border-sky-200",             icon: Star },
  intern:          { label: "Team-only",    color: "bg-rose-50 text-rose-800 border-rose-200",          icon: Lock },
};

const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fileIconFor = (name) => {
  const n = (name || "").toLowerCase();
  if (/\.(pdf)$/.test(n)) return FileText;
  if (/\.(xlsx|xls|csv)$/.test(n)) return FileSpreadsheet;
  if (/\.(jpg|jpeg|png|gif|webp|heic|heif)$/.test(n)) return ImageIcon;
  if (/\.(mp4|mov|m4v|avi|mkv|webm)$/.test(n)) return Video;
  if (/\.(dwg|dxf|svg)$/.test(n)) return MapIcon;
  if (/\.(doc|docx|sig)$/.test(n)) return FileSignature;
  return FileText;
};

function VisBadge({ visibility, size = "sm" }) {
  const meta = VIS_META[visibility] || VIS_META.nach_NDA;
  const Icon = meta.icon;
  const cls = size === "lg"
    ? "px-2.5 py-1 text-[11px]"
    : "px-2 py-0.5 text-[10px]";
  return (
    <span className={`inline-flex items-center gap-1 uppercase tracking-wider font-sans border ${cls} ${meta.color}`}>
      <Icon size={size === "lg" ? 12 : 10} strokeWidth={1.8} />
      {meta.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Filterbar
// ---------------------------------------------------------------------------
function FilterBar({ search, setSearch, visibilityFilter, setVisibilityFilter, sortMode, setSortMode, includeIntern, setIncludeIntern }) {
  return (
    <div className="border border-navy/12 bg-white px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="dr-acc-filterbar">
      <div className="relative flex-1 min-w-[220px] max-w-md">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-navy/40" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Datei suchen — über alle Rubriken…"
          className="w-full pl-8 pr-7 py-1.5 text-xs border border-navy/15 focus:outline-none focus:border-navy text-navy font-sans"
          data-testid="dr-acc-search"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy" aria-label="Suche löschen">
            <X size={12} />
          </button>
        )}
      </div>
      <select
        value={visibilityFilter}
        onChange={(e) => setVisibilityFilter(e.target.value)}
        className="px-2.5 py-1.5 text-xs border border-navy/15 bg-white text-navy focus:outline-none focus:border-navy font-sans"
        data-testid="dr-acc-vis-filter"
      >
        <option value="">Alle Sichtbarkeiten</option>
        <option value="sofort">Nur sofort sichtbar</option>
        <option value="nach_NDA">Nur „nach NDA"</option>
        <option value="nach_indikativ">Nur „nach Gebot"</option>
        <option value="intern">Nur Team-intern</option>
      </select>
      <select
        value={sortMode}
        onChange={(e) => setSortMode(e.target.value)}
        className="px-2.5 py-1.5 text-xs border border-navy/15 bg-white text-navy focus:outline-none focus:border-navy font-sans"
        data-testid="dr-acc-sort"
      >
        <option value="slug">Alphabetisch</option>
        <option value="count_desc">Meiste Dateien zuerst</option>
        <option value="size_desc">Größte zuerst</option>
        <option value="last_modified_desc">Zuletzt geändert</option>
      </select>
      <label className="inline-flex items-center gap-1.5 text-xs font-sans text-navy/75 cursor-pointer">
        <input
          type="checkbox"
          checked={includeIntern}
          onChange={(e) => setIncludeIntern(e.target.checked)}
          className="accent-navy"
          data-testid="dr-acc-intern-toggle"
        />
        Intern einschließen
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sektions-Header
// ---------------------------------------------------------------------------
function SectionHeader({ section, expanded, onToggle, onZip, isMatched }) {
  const Icon = expanded ? FolderOpen : Folder;
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggle();
    }
  };
  // Visueller Active-State: Header bei ausgeklappter Sektion hat Navy-Tint
  // und behält keine border-bottom unter sich → fließt visuell in den Body.
  // Linke Gold-Linie sitzt jetzt am äußeren <li>-Wrapper, nicht am Header,
  // damit sie über den gesamten ausgeklappten Bereich durchgezogen wirkt.
  const headerBg = expanded ? "bg-navy/[0.04]" : (isMatched ? "bg-gold/8" : "bg-white hover:bg-navy/[0.025]");
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={handleKey}
      className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-navy/20 ${headerBg} ${expanded ? "border-b border-gold/30" : ""}`}
      data-testid="dr-acc-section-header"
      aria-expanded={expanded}
    >
      <span className={`shrink-0 transition-colors ${expanded ? "text-gold" : "text-navy/55"}`}>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </span>
      <Icon size={18} className="text-gold shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-display font-semibold text-base truncate ${expanded ? "text-navy" : "text-navy"}`}>{section.name}</span>
          <span className="text-[10px] font-sans text-navy/55 tabular-nums">{section.slug}</span>
          <VisBadge visibility={section.default_visibility} />
        </div>
        <div className="text-[11px] font-sans text-navy/55 mt-0.5 flex items-center gap-3 flex-wrap">
          <span className="tabular-nums">
            {section.count} {section.count === 1 ? "Datei" : "Dateien"} · {fmtSize(section.total_size)}
          </span>
          {section.intern_count > 0 && (
            <span className="text-rose-700">
              <Lock size={10} className="inline mr-0.5" /> {section.intern_count} intern
            </span>
          )}
          {section.last_modified && (
            <span className="text-navy/55">
              <Calendar size={10} className="inline mr-0.5" />
              {fmtDate(section.last_modified)}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {section.count > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); onZip(); }}
            onKeyDown={(e) => e.stopPropagation()}
            title={`„${section.slug}" als ZIP herunterladen`}
            className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider text-navy/65 hover:text-navy hover:bg-navy/5 border border-navy/15 font-sans"
            data-testid="dr-acc-zip-btn"
          >
            <Archive size={12} /> ZIP
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Datei-Zeile
// ---------------------------------------------------------------------------
function FileRow({ file, onDownload, onEdit, onDelete }) {
  const Icon = fileIconFor(file.filename_original);
  // Effektive Visibility: visibility_override > default
  const effectiveVis = file.visibility_override || file.kategorie_visibility || "nach_NDA";
  return (
    <li
      data-testid="dr-acc-file-row"
      className="grid grid-cols-[20px_1fr_auto_auto_auto] sm:grid-cols-[20px_1fr_120px_120px_140px_auto] gap-2 sm:gap-3 items-center px-4 py-2 hover:bg-navy/3 border-t border-navy/5"
    >
      <Icon size={14} className="text-navy/50 shrink-0" />
      <div className="min-w-0">
        <div className="text-xs text-navy font-sans truncate" title={file.filename_original}>
          {file.filename_original}
        </div>
        {file.is_intern && (
          <span className="text-[10px] text-rose-700 font-sans inline-flex items-center gap-1">
            <Lock size={9} /> intern
          </span>
        )}
        {file.original_pfad && (
          <div className="text-[10px] text-navy/45 font-sans truncate" title={file.original_pfad}>
            {file.original_pfad}
          </div>
        )}
      </div>
      <span className="text-[11px] font-sans text-navy/65 tabular-nums text-right hidden sm:inline">
        {fmtSize(file.size_bytes)}
      </span>
      <span className="hidden sm:inline">
        <VisBadge visibility={file.visibility_override || effectiveVis} />
      </span>
      <span className="text-[10px] font-sans text-navy/55 hidden sm:inline truncate">
        {fmtDate(file.hochgeladen_am)} · {file.hochgeladen_von || "?"}
      </span>
      <div className="flex items-center gap-0.5 justify-end">
        <a
          href={onDownload(file.id)}
          className="p-1.5 text-navy/55 hover:text-navy"
          title="Download"
          data-testid="dr-acc-download"
        >
          <Download size={13} />
        </a>
        <button
          onClick={() => onEdit(file)}
          className="p-1.5 text-navy/55 hover:text-navy"
          title="Bearbeiten"
          data-testid="dr-acc-edit"
        >
          <Pencil size={13} />
        </button>
        <button
          onClick={() => onDelete(file)}
          className="p-1.5 text-navy/55 hover:text-rose-700"
          title="Löschen"
          data-testid="dr-acc-delete"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Sektions-Inhalt (lazy-load Datei-Liste)
// ---------------------------------------------------------------------------
function SectionBody({ section, onEdit, refreshKey, search }) {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    drListFiles({ kategorie: section.slug })
      .then((data) => {
        if (cancelled) return;
        let items = data.items || [];
        // Bei Suche client-seitig filtern
        if (search) {
          const q = search.toLowerCase();
          items = items.filter((f) =>
            (f.filename_original || "").toLowerCase().includes(q) ||
            (f.beschreibung || "").toLowerCase().includes(q) ||
            (f.original_pfad || "").toLowerCase().includes(q)
          );
        }
        // Inject kategorie default_visibility for VisBadge
        items = items.map((f) => ({ ...f, kategorie_visibility: section.default_visibility }));
        setFiles(items);
      })
      .catch(() => {
        if (!cancelled) toast.error("Dateien konnten nicht geladen werden");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [section.slug, section.default_visibility, refreshKey, search]);

  const handleDelete = async (file) => {
    if (!window.confirm(`Datei „${file.filename_original}" deaktivieren?\n(Soft-Delete: kann manuell wiederhergestellt werden.)`)) return;
    try {
      await drDeleteFile(file.id);
      toast.success("Datei deaktiviert");
      setFiles((prev) => (prev || []).filter((f) => f.id !== file.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-4 text-xs text-navy/55 font-sans">Lade…</div>
    );
  }
  if (!files || files.length === 0) {
    return (
      <div className="px-4 py-6 text-center" data-testid="dr-acc-empty">
        <div className="text-xs text-navy/55 font-sans mb-2">
          {section.count === 0 ? "Noch keine Dateien" : "Keine Treffer für aktuellen Filter"}
        </div>
        {section.count === 0 && (
          <div className="text-[11px] text-navy/45 font-sans">
            Tipp: Im Header „Datei hochladen" oder „Ordnerstruktur" wählen
          </div>
        )}
      </div>
    );
  }

  return (
    <ul className="divide-y divide-navy/5" data-testid="dr-acc-file-list">
      {/* Spalten-Header: sticky, damit User auch bei langen Listen (z. B. 142 Mietverträge)
          immer Spaltennamen + Klammer-Verortung im Blick hat. Top-Offset so gewählt,
          dass er knapp unter dem Sektion-Header sitzt. */}
      <li className="hidden sm:grid grid-cols-[20px_1fr_120px_120px_140px_auto] gap-3 px-4 py-1.5 text-[10px] uppercase tracking-wider font-sans text-navy/55 border-b border-navy/10 bg-ledger sticky top-0 z-[1]">
        <span></span>
        <span>Datei</span>
        <span className="text-right">Größe</span>
        <span>Sichtbarkeit</span>
        <span>Hochgeladen</span>
        <span className="text-right">Aktion</span>
      </li>
      {files.map((file) => (
        <FileRow
          key={file.id}
          file={file}
          onDownload={(id) => drTeamDownloadUrl(id)}
          onEdit={onEdit}
          onDelete={handleDelete}
        />
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Haupt-Komponente
// ---------------------------------------------------------------------------
export default function DataroomAccordion({ onEdit, onZip, refreshKey = 0 }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("");
  const [sortMode, setSortMode] = useState("slug");
  const [includeIntern, setIncludeIntern] = useState(true);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const data = await drCategoriesSummary({ includeIntern });
      setSummary(data);
    } catch (e) {
      toast.error("Akkordion konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [includeIntern]);

  useEffect(() => { loadSummary(); }, [loadSummary, refreshKey]);

  // Beim Suchen: Treffer-Sektionen automatisch ausklappen
  useEffect(() => {
    if (!search || !summary) return;
    // We don't know which sections have matches yet — let user expand manually,
    // OR auto-expand all sections with count > 0
    setExpanded(new Set(summary.items.filter((s) => s.count > 0).map((s) => s.slug)));
  }, [search, summary]);

  const filteredSections = useMemo(() => {
    if (!summary) return [];
    let items = summary.items;
    if (visibilityFilter) {
      items = items.filter((s) => s.default_visibility === visibilityFilter);
    }
    if (!includeIntern) {
      items = items.filter((s) => s.default_visibility !== "intern");
    }
    // Sort
    const sorted = [...items];
    if (sortMode === "count_desc") sorted.sort((a, b) => b.count - a.count);
    else if (sortMode === "size_desc") sorted.sort((a, b) => b.total_size - a.total_size);
    else if (sortMode === "last_modified_desc") {
      sorted.sort((a, b) => (b.last_modified || "").localeCompare(a.last_modified || ""));
    } else {
      sorted.sort((a, b) => a.slug.localeCompare(b.slug));
    }
    return sorted;
  }, [summary, visibilityFilter, includeIntern, sortMode]);

  // Wenn nur 1 nicht-leere Sektion: auto-expand — aber NUR beim allerersten Load,
  // sonst überschreibt das den User-Expansion-State nach jedem refreshKey-Bump.
  const initialAutoExpandDone = useRef(false);
  useEffect(() => {
    if (!summary || initialAutoExpandDone.current) return;
    const nonEmpty = summary.items.filter((s) => s.count > 0);
    if (nonEmpty.length === 1) {
      setExpanded(new Set([nonEmpty[0].slug]));
    }
    initialAutoExpandDone.current = true;
  }, [summary]);

  const toggle = (slug) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const totalFiles = summary?.total_count || 0;
  const totalSize = summary?.total_size || 0;
  const totalKats = filteredSections.length;
  const allExpanded = filteredSections.every((s) => s.count === 0 || expanded.has(s.slug));

  return (
    <div className="space-y-3" data-testid="dr-accordion">
      <FilterBar
        search={search}
        setSearch={setSearch}
        visibilityFilter={visibilityFilter}
        setVisibilityFilter={setVisibilityFilter}
        sortMode={sortMode}
        setSortMode={setSortMode}
        includeIntern={includeIntern}
        setIncludeIntern={setIncludeIntern}
      />

      {/* Quick-Summary */}
      <div className="flex items-center justify-between flex-wrap gap-3 text-xs font-sans text-navy/65" data-testid="dr-acc-summary">
        <div>
          <span className="font-display text-navy text-base tabular-nums">{totalFiles}</span> Dateien
          {" · "}
          <span className="font-display text-navy text-base tabular-nums">{fmtSize(totalSize)}</span>
          {" · "}
          {totalKats} Rubriken
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              const all = new Set(filteredSections.filter((s) => s.count > 0).map((s) => s.slug));
              setExpanded(allExpanded ? new Set() : all);
            }}
            className="text-[11px] uppercase tracking-wider hover:text-navy"
            data-testid="dr-acc-expand-all"
          >
            {allExpanded ? "Alle einklappen" : "Alle ausklappen"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-navy/55 py-8 text-center font-sans border border-dashed border-navy/15">
          Lade Rubriken…
        </div>
      )}

      {!loading && filteredSections.length === 0 && (
        <div className="text-sm text-navy/55 py-8 text-center font-sans border border-dashed border-navy/15">
          Keine Rubriken entsprechen dem Filter.
        </div>
      )}

      <ul className="space-y-3">
        {filteredSections.map((section) => {
          const isOpen = expanded.has(section.slug);
          return (
            <li
              key={section.slug}
              data-expanded={isOpen}
              data-testid="dr-acc-section"
              className={
                "bg-white transition-all " +
                (isOpen
                  // Ausgeklappt: linke Gold-Klammer durchgehend, dezenter Drop-Anker rechts
                  ? "border border-navy/15 border-l-[4px] border-l-gold"
                  // Eingeklappt: flach mit transparenter linker Spur (wird beim Hover Gold-zart)
                  : "border border-navy/12 border-l-[4px] border-l-transparent hover:border-l-gold/40")
              }
            >
              <SectionHeader
                section={section}
                expanded={isOpen}
                onToggle={() => toggle(section.slug)}
                onZip={() => onZip?.(section.slug)}
              />
              {isOpen && (
                <div
                  className="bg-navy/[0.015] pl-2 sm:pl-4"
                  data-testid="dr-acc-section-body"
                >
                  <SectionBody
                    section={section}
                    onEdit={onEdit}
                    refreshKey={refreshKey}
                    search={search}
                  />
                  {/* Footer-Anker: visueller Abschluss der Sektion */}
                  <div className="h-2 border-t border-gold/20 mt-1" aria-hidden="true" />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
